import React, { useCallback } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPercentCrop,
  convertToPixelCrop,
  Crop,
  makeAspectCrop,
  PercentCrop,
  PixelCrop,
} from 'react-image-crop';

import { Button, Modal } from '../../shared/Components';
import { colors } from '../../theme';
import { Text, Image, FlexContainer } from '../../shared/Typhography';
import 'react-image-crop/dist/ReactCrop.css';
import { imgPreview } from '../../services';
import { strings } from '../../strings';
import { useGlobalModalContext } from '../../context/GlobalModal';
import { PUZZLE_IMAGE } from '../../app-constants';
import { foregroundColors } from '../../shared/styling';
import { FileData } from '../../modules/dashboard/pages/CreatePuzzleScreen/CreatePuzzleScreen';

export function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: mediaWidth,
        height: mediaHeight,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  ) as PercentCrop; // Asserting PercentCrop because unit is hard-coded to '%' on line 4 of function.
}

const CropperModal = () => {
  const { hideModal, store } = useGlobalModalContext();
  const { modalProps } = store || {};
  const { img, title, handleSubmit, imageData } = modalProps || {};
  const [crop, setCrop] = React.useState<Crop>(modalProps.imageData.percentCropData);

  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const imgRef = React.useRef<HTMLImageElement | null>(null);

  // Width and height scale needed to set minimum crop to 2024px.
  let minWidth = 0;
  let minHeight = 0;

  // Current crop is applied to imageRef dimensions (around 300x300), we need to scale it
  // to be proportional to original.
  let scaledCrop: PixelCrop | null = null;
  let isHQImageAvailable = false;

  const imagePreviewDimensions = calculateImagePreviewDimensions(
    imageData.originalResolution.width,
    imageData.originalResolution.height,
  );

  if (imgRef.current) {
    if (!completedCrop) return;

    // Using getBoundingClientRect because it allows better precision.
    const boundingClientRect = imgRef.current.getBoundingClientRect();

    scaledCrop = scaleCrop({
      crop: crop,
      previewSize: { width: boundingClientRect.width, height: boundingClientRect.height },
      originalSize: {
        width: imageData.originalResolution.width,
        height: imageData.originalResolution.height,
      },
      outputUnit: 'px',
    }) as PixelCrop; // Asserting to PixelCrop because outputUnit is set to 'px'.

    minWidth =
      (boundingClientRect.width / imageData.originalResolution.width) *
      PUZZLE_IMAGE.DIGITAL.CROP.WIDTH;
    minHeight =
      (boundingClientRect.height / imageData.originalResolution.height) *
      PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT;

    // We don't need to check height because it is a square crop.
    isHQImageAvailable = Math.round(scaledCrop.width) >= PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH;
  }

  /**
   * Allows us to trigger completed crop change as soon as imgRef is assigned.
   * This prevents blank preview when user opens cropper but doesn't interact with it,
   * just accepts defaults.
   */
  const onImgRefChange = useCallback((node: HTMLImageElement) => {
    if (node === null) return;

    imgRef.current = node;

    setCrop(imageData.percentCropData);
    setCompletedCrop(convertToPixelCrop(imageData.percentCropData, node.width, node.height));
  }, []);

  const closeModal = () => {
    hideModal();
  };

  const submit = async () => {
    if (!completedCrop || !imgRef.current || !scaledCrop) return;

    const croppedImageUrl = await imgPreview(
      imgRef.current,
      convertToPixelCrop(crop, imgRef.current.width, imgRef.current.height),
    );

    const newImageData: FileData = {
      ...imageData,
      hasHqImage: Math.round(scaledCrop.width) >= PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH,
      url: croppedImageUrl,
      percentCropData: crop,
      pixelCropData: {
        x: Math.round(scaledCrop.x),
        y: Math.round(scaledCrop.y),
        width: Math.round(scaledCrop.width),
        height: Math.round(scaledCrop.height),
        unit: scaledCrop.unit,
      },
    };

    handleSubmit(newImageData);

    hideModal();
  };

  return (
    <Modal width={1200} closeModal={closeModal} onRequestClose={closeModal}>
      <Text size={1.2} color={colors.black} margin="0 0 16px 0" weight="bold">
        {title}
      </Text>
      <ReactCrop
        style={{
          border: `1px solid ${
            isHQImageAvailable ? foregroundColors.primary : foregroundColors.error
          }`,
        }}
        minWidth={minWidth}
        minHeight={minHeight}
        ruleOfThirds
        crop={crop}
        aspect={1}
        onComplete={(c) => setCompletedCrop(c)}
        onChange={(pixelCrop, c) => {
          setCrop({
            x: c.x,
            y: c.y,
            width: c.width,
            height: c.height,
            unit: '%',
          });
        }}
      >
        <Image
          style={{ width: imagePreviewDimensions.width, height: imagePreviewDimensions.height }}
          src={img}
          alt="Cropp"
          ref={onImgRefChange}
        />
      </ReactCrop>
      <FlexContainer width="100%" flexDirection="column">
        <Text textAlign="center" color={foregroundColors.secondary} size={0.9} margin="0 0 16px 0">
          Minimum resolution for high quality image is {PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH}
          px
        </Text>

        <Text textAlign="center" weight="bold">
          Crop size
        </Text>
        <FlexContainer width="100%" gap="20px" justifyContent="center">
          <Text color={isHQImageAvailable ? foregroundColors.primary : foregroundColors.error}>
            Width: {Math.round(scaledCrop?.width || 0)} {scaledCrop?.unit}
          </Text>
          <Text color={isHQImageAvailable ? foregroundColors.primary : foregroundColors.error}>
            Height: {Math.round(scaledCrop?.height || 0)} {scaledCrop?.unit}
          </Text>
        </FlexContainer>
        {!isHQImageAvailable && (
          <Text textAlign="center" color={foregroundColors.error}>
            High Quality puzzle image will not be available!
          </Text>
        )}
      </FlexContainer>

      <FlexContainer width="100%" flexDirection="column">
        <Text textAlign="center" weight="bold">
          Coordinates
        </Text>
        <FlexContainer width="100%" gap="20px" justifyContent="center">
          <span>
            X: {Math.round(scaledCrop?.x || 0)} {scaledCrop?.unit}
          </span>
          <span>
            Y: {Math.round(scaledCrop?.y || 0)} {scaledCrop?.unit}
          </span>
        </FlexContainer>
      </FlexContainer>
      <FlexContainer justifyContent="center" margin="16px 0 0 0">
        <Button variant="link" onClick={hideModal}>
          {strings.cancel}
        </Button>
        <Button
          onClick={submit}
          height={40}
          width={140}
          disabled={!isValidCrop(crop, imgRef.current?.width || 0, imgRef.current?.height || 0)}
        >
          {strings.submit}
        </Button>
      </FlexContainer>
    </Modal>
  );
};

export function isPercentCrop(crop: Crop | PixelCrop | PercentCrop): crop is PercentCrop {
  return crop.unit === '%';
}

type ScaleCropArgs = {
  /** Crop to scale */
  crop: Crop;
  /** Original dimensions of image */
  originalSize: {
    width: number;
    height: number;
  };
  /** Size of image inside of preview */
  previewSize: {
    width: number;
    height: number;
  };
  /** Preferred return crop type */
  outputUnit?: Crop['unit'];
};

/**
 * Adjust crop to original width and height.
 *
 * NOTE: Target width and height of original need to be proportional to preview.
 *       If not, preview is probably distorted and will result in distorted image.
 *
 */
export function scaleCrop(args: ScaleCropArgs) {
  const { crop, originalSize, previewSize, outputUnit = args.outputUnit || crop.unit } = args;
  let finalCrop = crop;

  // If aspect ratios don't match, something is not adding up properly.
  if (
    Math.round(originalSize.width / originalSize.height) !==
    Math.round(previewSize.width / previewSize.height)
  ) {
    throw new Error('error/aspect-ratio-not-matching');
  }

  const widthScale = originalSize.width / previewSize.width;
  const heightScale = originalSize.height / previewSize.height;

  if (finalCrop.unit === '%') {
    // Having crop in percentages makes width and height difference irrelevant
    // as long as aspect ratio is same.
    finalCrop = {
      x: crop.x,
      y: crop.y,
      width: crop.width,
      height: crop.height,
      unit: '%',
    };
  } else {
    // For pixels unit we need to scale crop dimensions to desired dimensions.
    finalCrop = {
      x: crop.x * widthScale,
      y: crop.y * heightScale,
      width: Math.floor(crop.width * widthScale),
      height: Math.floor(crop.height * heightScale),
      unit: 'px',
    };
  }

  return outputUnit === '%'
    ? convertToPercentCrop(finalCrop, originalSize.width, originalSize.height)
    : convertToPixelCrop(finalCrop, originalSize.width, originalSize.height);
}

export function isValidCrop(crop: Crop, maxWidth: number, maxHeight: number) {
  if (crop.unit !== '%' && crop.unit !== 'px') return false;

  if (crop.unit === '%') {
    if (crop.width <= 0 || crop.height <= 0) return false;
    if (crop.width > 100 || crop.height > 100) return false;
    if (crop.x > 100 || crop.y > 100) return false;
  }

  if (crop.unit === 'px') {
    if (
      crop.width < PUZZLE_IMAGE.DIGITAL.CROP.WIDTH ||
      crop.height < PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT
    )
      return false;
    if (crop.width > maxWidth || crop.height > maxHeight) return false;
    if (crop.x > maxWidth || crop.y > maxHeight) return false;
  }

  return true;
}

/**
 * Calculates image preview dimensions while keeping aspect-ratio.
 * Allows to have larger preview without squishing image.
 *
 * @param originalWidth
 * @param originalHeight
 */
function calculateImagePreviewDimensions(originalWidth: number, originalHeight: number) {
  const maxPreviewWidth = 1000;
  const maxPreviewHeight = 1200;
  const orientation = originalWidth >= originalHeight ? 'landscape' : 'portrait';

  const previewDimensions = { width: 0, height: 0 };

  if (orientation === 'landscape') {
    previewDimensions.width = maxPreviewWidth;
    previewDimensions.height = (maxPreviewWidth / originalWidth) * originalHeight;
  } else {
    previewDimensions.width = (maxPreviewHeight / originalHeight) * originalWidth;
    previewDimensions.height = maxPreviewHeight;
  }

  return previewDimensions;
}

export default CropperModal;
